import { ChevronLeft, ChevronRight } from "lucide-react";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { cn } from "@/lib/utils";

import { Button } from "@/components/ui/button";

interface PaginationProps {
  data: { totalCount: number };
  onChange: (page: number, rowsPerPage: number) => void;
  defaultPerPage: number;
  cPage: number;
  className?: string;
}

/**
 * Compact pagination: "1–25 of 132" with previous / next buttons.
 * Renders nothing when everything fits on one page.
 */
const Pagination = ({
  className,
  data,
  onChange,
  defaultPerPage,
  cPage,
}: PaginationProps) => {
  const { t } = useTranslation();
  const [rowsPerPage, setRowsPerPage] = useState(defaultPerPage || 15);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    if (defaultPerPage) {
      setRowsPerPage(defaultPerPage);
    }
    if (cPage) {
      setCurrentPage(parseInt(`${cPage}`));
    }
  }, [defaultPerPage, cPage]);

  const totalCount = data.totalCount;
  if (!totalCount || totalCount <= rowsPerPage) {
    return null;
  }
  const totalPage = Math.ceil(totalCount / rowsPerPage);
  const start = (currentPage - 1) * rowsPerPage + 1;
  const end = Math.min(currentPage * rowsPerPage, totalCount);

  const goToPage = (page: number) => {
    setCurrentPage(page);
    onChange(page, rowsPerPage);
    const pageContainer = window.document.getElementById("pages");
    pageContainer?.scroll({ top: 0, left: 0 });
  };

  return (
    <div
      data-slot="pagination"
      className={cn(
        "flex items-center justify-between gap-3 text-sm text-gray-600",
        className,
      )}
    >
      <span>{t("pagination_range", { start, end, total: totalCount })}</span>
      <div className="flex items-center gap-1">
        <Button
          id="prev-pages"
          variant="outline"
          size="icon"
          className="size-8"
          aria-label={t("previous")}
          disabled={currentPage <= 1}
          onClick={() => goToPage(currentPage - 1)}
        >
          <ChevronLeft className="size-4" />
        </Button>
        <span className="min-w-12 text-center tabular-nums">
          {currentPage} / {totalPage}
        </span>
        <Button
          id="next-pages"
          variant="outline"
          size="icon"
          className="size-8"
          aria-label={t("next")}
          disabled={currentPage >= totalPage}
          onClick={() => goToPage(currentPage + 1)}
        >
          <ChevronRight className="size-4" />
        </Button>
      </div>
    </div>
  );
};

export default Pagination;
